/**
 * SVG icon functions for the Galaxy design system.
 *
 * Each function returns an inline SVG string. Icons use currentColor
 * so they inherit the parent's text color via CSS.
 *
 * Standard attributes: 18x18, stroke-based, rounded caps/joins.
 */

const ATTRS =
  'xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"'

/** Fleet grid icon (4 squares) */
export function iconFleet(size = 18): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${ATTRS}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>`
}

/** Work items checkbox icon */
export function iconWork(size = 18): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${ATTRS}><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>`
}

/** Knowledge base book icon */
export function iconKB(size = 18): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${ATTRS}><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/></svg>`
}

/** Galaxy/sun toggle icon */
export function iconGalaxy(size = 16): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>`
}

/** Honeycomb trace icon */
export function iconTrace(size = 10): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>`
}

/** Dashboard icon */
export function iconDashboard(size = 10): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>`
}

/** Logs/document icon */
export function iconLogs(size = 10): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>`
}

/** Knowledge graph icon — three linked nodes */
export function iconGraph(size = 18): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${ATTRS}><circle cx="5" cy="6" r="2.5"/><circle cx="19" cy="9" r="2.5"/><circle cx="10" cy="18" r="2.5"/><line x1="7.3" y1="6.6" x2="16.7" y2="8.5"/><line x1="17.9" y1="11.2" x2="11.6" y2="16"/><line x1="6.3" y1="8.2" x2="8.9" y2="15.6"/></svg>`
}

/** Analytics icon — rising bars */
export function iconAnalytics(size = 18): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${ATTRS}><line x1="3" y1="21" x2="21" y2="21"/><line x1="7" y1="21" x2="7" y2="13"/><line x1="12" y1="21" x2="12" y2="8"/><line x1="17" y1="21" x2="17" y2="4"/></svg>`
}

/** Back arrow icon */
export function iconBack(size = 14): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${ATTRS}><polyline points="15 18 9 12 15 6"/></svg>`
}

/** Copy/clipboard icon */
export function iconCopy(size = 12): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" ${ATTRS}><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/></svg>`
}

/** Status icon for phase nodes: checkmark, dot, cross, or dash */
export function phaseIcon(status: 'done' | 'running' | 'failed' | 'pending'): string {
  switch (status) {
    case 'done':
      return '&#10003;'
    case 'running':
      return '&#9679;'
    case 'failed':
      return '&#10007;'
    default:
      return '-'
  }
}
